import { loadStripe } from '@stripe/stripe-js';
import type { PaymentRequest } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { AnimatePresence } from 'motion/react';
import { useEffect, useState } from 'react';
import { config } from '../../../../config';
import { Modal } from '../../../../modules/design-system/Modal/Modal';

const stripePromise = loadStripe(config.stripeKey);

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1a1a1a',
      fontFamily: 'system-ui, sans-serif',
      '::placeholder': { color: '#a0a0a0' },
    },
    invalid: { color: '#e53e3e' },
  },
};

async function createPaymentIntent(amountCents: number): Promise<string> {
  const body = new URLSearchParams({
    amount: String(amountCents),
    currency: 'usd',
    'payment_method_types[]': 'card',
  });
  const res = await fetch('https://api.stripe.com/v1/payment_intents', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${config.stripeSecretKey}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });
  const data = await res.json() as { client_secret: string };
  return data.client_secret;
}

type Status = 'idle' | 'loading' | 'success' | 'error';

interface FormProps {
  clientSecret: string;
  priceLabel: string;
  amountCents: number;
  onClose: () => void;
  onSuccess: () => void;
}

function PaymentForm({ clientSecret, priceLabel, amountCents, onClose, onSuccess }: FormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [applePayStatus, setApplePayStatus] = useState<string>('checking…');

  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: { label: 'Pack purchase', amount: amountCents },
      requestPayerName: false,
      requestPayerEmail: false,
    });

    void pr.canMakePayment().then(result => {
      if (!result) {
        setApplePayStatus('❌ unavailable — not Safari, no card in Wallet, HTTP, or domain not verified');
      } else if (!result.applePay) {
        setApplePayStatus(`❌ applePay=false — result: ${JSON.stringify(result)}`);
      } else {
        setApplePayStatus('✅ available');
      }
      if (result?.applePay) setPaymentRequest(pr);
    });

    pr.on('paymentmethod', async event => {
      const { paymentIntent, error } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: event.paymentMethod.id },
        { handleActions: false },
      );

      if (error) {
        event.complete('fail');
        setStatus('error');
        setMessage(error.message ?? 'Payment failed');
        return;
      }

      event.complete('success');

      if (paymentIntent.status === 'requires_action') {
        const { error: actionError } = await stripe.confirmCardPayment(clientSecret);
        if (actionError) {
          setStatus('error');
          setMessage(actionError.message ?? 'Payment failed');
        } else {
          setStatus('success');
          setMessage(`Payment ID: ${paymentIntent.id}`);
        }
      } else {
        setStatus('success');
        setMessage(`Payment ID: ${paymentIntent.id}`);
      }
    });
  }, [stripe, clientSecret, amountCents]);

  async function handleCardSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!stripe || !elements) return;

    const card = elements.getElement(CardElement);
    if (!card) return;

    setStatus('loading');
    setMessage('');

    const { paymentIntent, error } = await stripe.confirmCardPayment(
      clientSecret,
      { payment_method: { card } },
    );

    if (error) {
      setStatus('error');
      setMessage(error.message ?? 'Payment failed');
    } else if (paymentIntent?.status === 'succeeded') {
      setStatus('success');
      setMessage(`Payment ID: ${paymentIntent.id}`);
    }
  }

  if (status === 'success') {
    return (
      <div className="rounded-2xl bg-white p-6 flex flex-col items-center gap-4">
        <div className="text-4xl">✓</div>
        <p className="text-lg font-bold text-gray-900">Test payment OK</p>
        <p className="text-xs text-gray-400 font-mono break-all text-center">
          {message}
        </p>
        <button
          type="button"
          onClick={() => { onClose(); onSuccess(); }}
          className="mt-2 w-full rounded-xl bg-[var(--ui-accent,#22c55e)] px-4 py-3 text-white font-semibold"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => void handleCardSubmit(e)}
      className="rounded-2xl bg-white p-6 flex flex-col gap-5"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Pay {priceLabel}</h2>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
        >
          ×
        </button>
      </div>

      {paymentRequest && (
        <>
          <PaymentRequestButtonElement
            options={{ paymentRequest, style: { paymentRequestButton: { height: '48px' } } }}
          />
          <div className="flex items-center gap-3">
            <span className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">or pay with card</span>
            <span className="flex-1 h-px bg-gray-200" />
          </div>
        </>
      )}

      <div className="rounded-xl border border-gray-200 px-4 py-3">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>

      {status === 'error' && (
        <p className="text-sm text-red-500">{message}</p>
      )}

      <button
        type="submit"
        disabled={!stripe || status === 'loading'}
        className="
          w-full rounded-xl bg-[var(--ui-accent,#22c55e)] px-4 py-3
          text-white font-semibold
          disabled:opacity-60 disabled:cursor-not-allowed
          transition-opacity
        "
      >
        {status === 'loading' ? 'Processing…' : `Pay ${priceLabel}`}
      </button>

      <p className="text-xs text-gray-400 text-center">
        Test mode — use card <span className="font-mono">4242 4242 4242 4242</span>
      </p>

      <p className="text-xs text-gray-500 text-center break-all">
        Apple Pay: {applePayStatus}
      </p>
    </form>
  );
}

export interface StripePaymentModalProps {
  priceLabel: string;
  amountCents: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function StripePaymentModal({
  priceLabel,
  amountCents,
  onClose,
  onSuccess,
}: StripePaymentModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  useEffect(() => {
    createPaymentIntent(amountCents)
      .then(setClientSecret)
      .catch(() => setClientSecret(''));
  }, [amountCents]);

  return (
    <AnimatePresence>
      <Modal onClose={onClose} inspectName="StripePaymentModal">
        {clientSecret === null && (
          <div className="rounded-2xl bg-white p-6 text-center text-gray-400">
            Loading…
          </div>
        )}
        {clientSecret !== null && clientSecret !== '' && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
              clientSecret={clientSecret}
              priceLabel={priceLabel}
              amountCents={amountCents}
              onClose={onClose}
              onSuccess={onSuccess}
            />
          </Elements>
        )}
        {clientSecret === '' && (
          <div className="rounded-2xl bg-white p-6 text-center text-red-500">
            Failed to initialise payment. Try again.
          </div>
        )}
      </Modal>
    </AnimatePresence>
  );
}
